/**
 * Complete Events Calendar with filters, drawer, and URL management
 * Self-contained component - just pass events and optional editButton
 * Handles all internal state, URL sync, theme loading, and drawer
 */

'use client';

import {
    CalendarDaysIcon,
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    LayoutGrid,
    List,
    Table,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CalendarViewMode } from '../calendar';
import { DayView, ListView, MonthView, WeekView } from '../calendar';
import { EventDrawerProvider } from '../cards/event-card';
import { EventsComponentsProvider } from '../config';
import { EventsFilters } from '../filters';
import { useEventsFilter, useThemeEnrichment } from '../hooks';
import type { Event } from '../types';
import { darkenHexColorWithOpacity, extractThemeIdsFromEvents } from '../utils';
import { EventsList } from '../views';
import { DefaultEventEditButton } from './drupal-edit-button';

export interface EventsCalendarProps {
  /** Array of events to display */
  events: Event[];
  /** Initial calendar view mode */
  initialView?: CalendarViewMode;
  /** Initial date to display */
  initialDate?: Date;
  /** Base URL for event links */
  baseUrl?: string;
  /** Available view modes to display (defaults to all: month, week, day, grid, list). If only one view is provided, navigation bar is hidden. */
  availableViews?: CalendarViewMode[];
  /** Days to hide (0=Sunday, 6=Saturday) */
  hiddenDays?: number[];
  /** Maximum events per day in month view */
  maxEventsPerDay?: number;
  /** Optional edit button render function (defaults to DefaultEventEditButton) */
  editButton?: (event: Event) => ReactNode;
  /** Filter events by theme names (case-insensitive) - pre-filters events before display */
  themeNamesInclude?: string[];
  /** Pre-filter by event type names - applies dynamic client-side filtering on mount */
  eventTypeNames?: string[];
  /** Show past events by default - controls initial visibility of past events */
  showPastEvents?: boolean;
  /** Control which filters are displayed. If all filters are false, the sidebar is hidden and calendar takes full width. */
  enabledFilters?: {
    search?: boolean;
    eventTypes?: boolean;
    themes?: boolean;
    pastEvents?: boolean;
  };
}

/**
 * Complete self-contained Events Calendar
 *
 * Features:
 * - Multiple view modes (month, week, day, grid, list)
 * - URL-synced view state
 * - Theme filtering with lazy loading
 * - Event type filtering
 * - Search
 * - Integrated drawer for event details
 * - Optional edit buttons
 *
 * @example
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   initialView="month"
 *   baseUrl="/events"
 *   editButton={(event) => <MyEditButton event={event} />}
 * />
 * ```
 *
 * @example Filter by theme names
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   themeNamesInclude={["safety", "human"]}
 * />
 * ```
 *
 * @example Pre-filter by event type names and show past events
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   eventTypeNames={["Work meeting", "Training", "Workshop - seminar"]}
 *   showPastEvents={true}
 * />
 * ```
 *
 * @example Limit available view modes
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   availableViews={["month", "grid", "list"]}
 * />
 * ```
 *
 * @example Single view mode (hides navigation bar)
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   availableViews={["grid"]}
 * />
 * ```
 *
 * @example Disable all filters (hides sidebar, calendar takes full width)
 * ```tsx
 * <EventsCalendar
 *   events={events}
 *   enabledFilters={{
 *     search: false,
 *     eventTypes: false,
 *     themes: false,
 *     pastEvents: false,
 *   }}
 * />
 * ```
 */
export function EventsCalendar({
  events,
  initialView = 'month',
  initialDate = new Date(),
  baseUrl = '/events',
  availableViews = ['month', 'week', 'day', 'grid', 'list'],
  hiddenDays,
  maxEventsPerDay,
  editButton,
  themeNamesInclude,
  eventTypeNames,
  showPastEvents,
  enabledFilters,
}: EventsCalendarProps) {
  const searchParams = useSearchParams();
  const [view, setView] = useState<CalendarViewMode>(initialView);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [searchQuery, setSearchQuery] = useState('');

  // Use DefaultEventEditButton if no custom editButton provided
  const defaultEditButton = useCallback(
    (event: Event) => <DefaultEventEditButton event={event} />,
    []
  );
  const finalEditButton = editButton || defaultEditButton;

  // Pre-filter events by theme names if provided
  const filteredEvents = useMemo(() => {
    if (!themeNamesInclude || themeNamesInclude.length === 0) {
      return events;
    }

    const normalizedThemeNames = themeNamesInclude.map(name => name.toLowerCase().trim());

    return events.filter(event => {
      if (!event.theme || event.theme.length === 0) {
        return false;
      }

      return event.theme.some(theme =>
        normalizedThemeNames.includes(theme.name.toLowerCase().trim())
      );
    });
  }, [events, themeNamesInclude]);

  // Extract theme IDs from filtered events for lazy loading
  const themeIds = useMemo(() => extractThemeIdsFromEvents(filteredEvents), [filteredEvents]);

  // Lazy load theme enrichment data (colors, hierarchy)
  const { themes: enrichedThemes } = useThemeEnrichment(themeIds);

  // Sync view state with URL query parameter using History API (no refresh)
  const handleViewChange = useCallback(
    (newView: CalendarViewMode) => {
      setView(newView);

      // Update URL silently using History API
      const params = new URLSearchParams(searchParams.toString());
      params.set('view', newView);
      const newUrl = `${baseUrl}?${params.toString()}`;

      window.history.replaceState({ ...window.history.state, view: newView }, '', newUrl);
    },
    [searchParams, baseUrl]
  );

  // Initialize view from URL on mount
  useEffect(() => {
    const viewParam = searchParams.get('view') as CalendarViewMode | null;
    if (viewParam && availableViews.includes(viewParam)) {
      setView(viewParam);
    }
  }, [searchParams, availableViews]);

  // Navigation helpers
  const navigateToday = () => setCurrentDate(new Date());

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  // Period label for display
  const periodLabel = useMemo(() => {
    switch (view) {
      case 'month':
        return currentDate.toLocaleDateString('en-GB', {
          month: 'long',
          year: 'numeric',
        });
      case 'week': {
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${weekStart.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        })} - ${weekEnd.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`;
      }
      case 'day':
        return currentDate.toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      case 'grid':
        return 'All Events - Grid';
      case 'list':
        return 'All Events - List';
      default:
        return '';
    }
  }, [currentDate, view]);

  // Use events filter hook with pre-filtered events
  const {
    query,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents: showPastEventsState,
    eventTypes: availableEventTypes,
    themes: filteredThemes,
    visibleEvents,
    totalResults,
    hasActiveFilters,
    setQuery,
    toggleEventType,
    toggleTheme,
    toggleThemes,
    clearEventTypeSelection,
    clearThemeSelection,
    setThemeFilterMode,
    setShowPastEvents,
    clearAllFilters,
  } = useEventsFilter({
    events: filteredEvents,
    pageSize: 1000,
    themes: enrichedThemes,
    initialEventTypeNames: eventTypeNames,
    initialShowPastEvents: showPastEvents,
  });

  // Sort events by date for list view
  const sortedEvents = useMemo(() => {
    return [...visibleEvents].sort((a, b) => {
      const aStart = a.eventDateTimeRange?.start?.timestamp || 0;
      const bStart = b.eventDateTimeRange?.start?.timestamp || 0;
      return aStart - bStart;
    });
  }, [visibleEvents]);

  // Check if at least one filter is enabled
  const hasAnyFilterEnabled = useMemo(() => {
    if (!enabledFilters) return true; // Default: show filters if not specified
    return Object.values(enabledFilters).some(value => value === true);
  }, [enabledFilters]);

  // View button configurations
  const viewConfigs: Record<CalendarViewMode, { icon: typeof CalendarDaysIcon; label: string }> = {
    month: { icon: CalendarDaysIcon, label: 'Month' },
    week: { icon: Table, label: 'Week' },
    day: { icon: CalendarIcon, label: 'Day' },
    grid: { icon: LayoutGrid, label: 'Grid' },
    list: { icon: List, label: 'List' },
  };

  return (
    <EventsComponentsProvider
      config={{
        searchQuery: query,
        baseUrl: baseUrl,
        darkenHexColorWithOpacity: darkenHexColorWithOpacity,
        LinkComponent: Link,
        ImageComponent: Image,
      }}
    >
      <EventDrawerProvider editButton={finalEditButton}>
        <div className="space-y-4">
          {/* Header with controls - only show if multiple views available */}
          {availableViews.length > 1 && (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
              {/* View switcher */}
              <div className="flex items-center gap-1 rounded-lg border bg-background p-1">
                {availableViews.map(viewMode => {
                  const config = viewConfigs[viewMode];
                  const Icon = config.icon;
                  return (
                    <button
                      key={viewMode}
                      onClick={() => handleViewChange(viewMode)}
                      className={`flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colours ${
                        view === viewMode ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{config.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={navigateToday}
                  className="rounded-lg border bg-background px-4 py-2 text-sm font-medium transition-colours hover:bg-accent"
                >
                  Today
                </button>

                {view !== 'list' && view !== 'grid' && (
                  <>
                    <button
                      onClick={navigatePrevious}
                      className="rounded-lg border bg-background p-2 transition-colours hover:bg-accent"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={navigateNext}
                      className="rounded-lg border bg-background p-2 transition-colours hover:bg-accent"
                      aria-label="Next"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}

                <h2 className="ml-2 text-lg font-semibold">{periodLabel}</h2>
              </div>
            </div>
          )}

          {/* Calendar view content with filters */}
          <div
            className={`grid gap-6 w-full max-w-full ${
              hasAnyFilterEnabled ? 'lg:grid-cols-[minmax(0,1fr)_320px]' : ''
            }`}
          >
            {/* Calendar/Events Content */}
            <div className="lg:col-start-1 w-full min-w-0">
              <div className="">
                {view === 'month' && (
                  <MonthView
                    events={visibleEvents}
                    currentDate={currentDate}
                    baseUrl={baseUrl}
                    hiddenDays={hiddenDays}
                    maxEventsPerDay={maxEventsPerDay}
                  />
                )}
                {view === 'week' && (
                  <WeekView events={visibleEvents} currentDate={currentDate} baseUrl={baseUrl} />
                )}
                {view === 'day' && (
                  <DayView events={visibleEvents} currentDate={currentDate} baseUrl={baseUrl} />
                )}
                {view === 'grid' && (
                  <EventsList
                    events={sortedEvents}
                    baseUrl={baseUrl}
                    showFilters={false}
                    pageSize={500}
                    initialShowPastEvents={true}
                  />
                )}
                {view === 'list' && <ListView events={sortedEvents} baseUrl={baseUrl} />}
              </div>
            </div>

            {/* Filters Sidebar - only show if at least one filter is enabled */}
            {hasAnyFilterEnabled && (
              <aside className="w-full lg:col-start-2 lg:row-start-1 lg:w-80 lg:justify-self-end">
                <EventsFilters
                  query={query}
                  onQueryChange={setQuery}
                  eventTypes={availableEventTypes}
                  selectedEventTypes={selectedEventTypes}
                  onToggleEventType={toggleEventType}
                  onClearEventTypeSelection={clearEventTypeSelection}
                  themes={filteredThemes}
                  selectedThemes={selectedThemes}
                  onToggleTheme={toggleTheme}
                  onToggleThemes={toggleThemes}
                  onClearThemeSelection={clearThemeSelection}
                  themeFilterMode={themeFilterMode}
                  onThemeFilterModeChange={setThemeFilterMode}
                  showPastEvents={showPastEventsState}
                  onShowPastEventsChange={setShowPastEvents}
                  totalResults={totalResults}
                  hasActiveFilters={hasActiveFilters}
                  onClearAllFilters={clearAllFilters}
                  enabledFilters={enabledFilters}
                />
              </aside>
            )}
          </div>
        </div>
      </EventDrawerProvider>
    </EventsComponentsProvider>
  );
}
